/**
 * Node Registry (Canvas V2)
 *
 * Central registry for all canvas node types.
 * Maps type strings to React components (wrapped with error boundaries),
 * schema objects, and provides helper functions.
 *
 * Key V2 changes from V1:
 * - crypto.randomUUID() for node IDs (BUILD_ORDER Q2)
 * - JSON-only schemas (BUILD_ORDER Q4)
 * - No static nodeCategories map (BUILD_ORDER Q5)
 * - PlaceholderNode for types not yet ported to canvas-v2
 */

import { memo, useEffect } from 'react'
import { Position, type NodeProps } from 'reactflow'
import { AlertTriangle } from 'lucide-react'
import { withNodeErrorBoundary } from './NodeErrorBoundary'
import { TypedHandle } from './TypedHandle'
import { createDebug } from '@ponpon/api-client'

const sizeLog = createDebug('dw:size:registry')
const unknownLog = createDebug('dw:canvas:unknown-node')

// Sprint 3: Designer node components
import { CharacterDesignerNode } from './nodes/CharacterDesignerNode'
import { LocationDesignerNode } from './nodes/LocationDesignerNode'
import { ObjectDesignerNode } from './nodes/ObjectDesignerNode'

// Sprint 4: Upload + Display node components
import { ImageUploadNode } from './nodes/ImageUploadNode'

// Sprint 6: Image Generation nodes
import { ImageGeneratorNode } from './nodes/ImageGeneratorNode'
import { VideoUploadNode } from './nodes/VideoUploadNode'
import { AudioUploadNode } from './nodes/AudioUploadNode'

// Sprint 7: Video Generation node
import { VideoGeneratorNode } from './nodes/VideoGeneratorNode'

// Sprint 11: Sound Expert node
import { SoundExpertNode } from './nodes/SoundExpertNode'

// Sprint 18b: Video Editor node
import { VideoEditorNode } from './nodes/VideoEditorNode'
import { CaptionerNode } from './nodes/CaptionerNode'
import { SingingPortraitNode } from './nodes/SingingPortraitNode'
import { BrandFilmNode } from './nodes/BrandFilmNode'

// Sprint 20: Script Writer node
import { ScriptWriterNode } from './nodes/ScriptWriterNode'

// Group node
import { GroupNode } from './nodes/GroupNode'

// Storyboard Designer node
import { StoryboardDesignerNode } from './nodes/StoryboardDesignerNode'

// JSON schema imports (all schemas are JSON-only per BUILD_ORDER Q4)
import characterDesignerSchema from '../../../../../schemas/nodes/characterDesigner.json'
import locationDesignerSchema from '../../../../../schemas/nodes/locationDesigner.json'
import objectDesignerSchema from '../../../../../schemas/nodes/objectDesigner.json'
import imageGeneratorSchema from '../../../../../schemas/nodes/imageGenerator.json'
import videoGeneratorSchema from '../../../../../schemas/nodes/videoGenerator.json'
import scriptWriterSchema from '../../../../../schemas/nodes/scriptWriter.json'
import screenplayWriterSchema from '../../../../../schemas/nodes/screenplayWriter.json'
import storyboardDesignerSchema from '../../../../../schemas/nodes/storyboardDesigner.json'
import storyboardNoEnhanceDesignerSchema from '../../../../../schemas/nodes/storyboardNoEnhanceDesigner.json'
import videoEditorSchema from '../../../../../schemas/nodes/videoEditor.json'
import generatedImageWithHistorySchema from '../../../../../schemas/nodes/generatedImageWithHistory.json'
import webtoonPanelGeneratorSchema from '../../../../../schemas/nodes/webtoonPanelGenerator.json'
import webtoonGroupSchema from '../../../../../schemas/nodes/webtoonGroup.json'
import shortVideoGroupSchema from '../../../../../schemas/nodes/shortVideoGroup.json'
import groupSchema from '../../../../../schemas/nodes/nodeGroup.json'

// New JSON schemas created for Input/Media categories
import imageSchema from '../../../../../schemas/nodes/image.json'
import videoSchema from '../../../../../schemas/nodes/video.json'
import audioSchema from '../../../../../schemas/nodes/audio.json'
import textSchema from '../../../../../schemas/nodes/text.json'
import promptInputSchema from '../../../../../schemas/nodes/promptInput.json'
import panelSourceSchema from '../../../../../schemas/nodes/panelSource.json'
import directorSchema from '../../../../../schemas/nodes/director.json'
import soundExpertSchema from '../../../../../schemas/nodes/soundExpert.json'
import hyperframeRendererSchema from '../../../../../schemas/nodes/hyperframeRenderer.json'
import remotionComposerSchema from '../../../../../schemas/nodes/remotionComposer.json'
import captionerSchema from '../../../../../schemas/nodes/captioner.json'
import singingPortraitSchema from '../../../../../schemas/nodes/singingPortrait.json'
import brandFilmSchema from '../../../../../schemas/nodes/brandFilm.json'

// =============================================================================
// Types
// =============================================================================

type SchemaValue = string | number | boolean | null | object | undefined

export interface PortDef {
  id: string
  type: string
  label: string
  description?: string
  required?: boolean
  multi?: boolean
  default?: SchemaValue
  options?: string[]
}

export type NodeDataValue = SchemaValue | File
export type NodeDataRecord = Record<string, NodeDataValue>
type SchemaConfig = Record<string, SchemaValue>

export interface NodeSchema {
  type: string
  label: string
  category: string
  icon?: string
  description?: string
  inputs: PortDef[]
  outputs: PortDef[]
  defaults: Record<string, SchemaValue>
  backendNodeId?: string
  backendType?: string
  executable?: boolean
  layout?: Record<string, { width: number; height: number }>
  footerHeight?: number
  config?: SchemaConfig[]
}

interface RawNodeSchema {
  type: string
  label: string
  category: string
  icon?: string
  description?: string
  inputs?: PortDef[]
  outputs?: PortDef[]
  backendNodeId?: string
  backendType?: string
  executable?: boolean
  layout?: Record<string, { width: number; height: number }>
  footerHeight?: number
  config?: SchemaConfig[]
}

// =============================================================================
// Schema Derivation
// =============================================================================

/**
 * Maps a port definition to its data field name.
 * The port ID IS the data field name — no translation needed.
 * @deprecated Use port.id directly. Kept for backward compatibility.
 */
export function portIdToDataField(input: { id: string }): string {
  return input.id
}

/**
 * Enrich a raw JSON schema with computed defaults.
 */
function enrichSchema(raw: RawNodeSchema): NodeSchema {
  const inputs = raw.inputs ?? []
  const outputs = raw.outputs ?? []
  const defaults: Record<string, SchemaValue> = {}
  for (const input of inputs) {
    if ('default' in input) {
      defaults[portIdToDataField(input)] = input.default
    }
  }
  return {
    ...raw,
    inputs,
    outputs,
    defaults,
  }
}

// =============================================================================
// Schema Registry (JSON-only, no inline TypeScript schemas)
// =============================================================================

export const nodeSchemas: Record<string, NodeSchema> = {
  // Agentic nodes
  characterDesigner: enrichSchema(characterDesignerSchema),
  locationDesigner: enrichSchema(locationDesignerSchema),
  objectDesigner: enrichSchema(objectDesignerSchema),
  imageGenerator: enrichSchema(imageGeneratorSchema),
  videoGenerator: enrichSchema(videoGeneratorSchema),
  scriptWriter: enrichSchema(scriptWriterSchema),
  screenplayWriter: enrichSchema(screenplayWriterSchema),
  storyboardDesigner: enrichSchema(storyboardDesignerSchema),
  storyboardNoEnhanceDesigner: enrichSchema(storyboardNoEnhanceDesignerSchema),
  videoEditor: enrichSchema(videoEditorSchema),
  webtoonPanelGenerator: enrichSchema(webtoonPanelGeneratorSchema),
  soundExpert: enrichSchema(soundExpertSchema),
  director: enrichSchema(directorSchema),
  // HyperFrames renderer — uses PlaceholderNode chrome for v1 (schema-driven
  // handles + status indicator). Custom UI with HTML editor + MP4 preview
  // ships in a follow-up; the backend agent + execution flow are already
  // wired so the Director can populate in_template_html / in_props_json
  // and trigger Generate via the standard executeNode path.
  hyperframeRenderer: enrichSchema(hyperframeRendererSchema),
  // Remotion composer — same PlaceholderNode chrome strategy as
  // hyperframeRenderer. Backend agent + Director tool +
  // remotion-sidecar are wired (see services/ads-renderer/ and
  // agents/remotion_composer/agent.py). A bespoke ad-result React
  // component (inline 9:16 player + manifest summary) is a Phase 1.0
  // frontend follow-up — until it lands users hit Generate via the
  // standard executeNode path and the result MP4 appears in the node's
  // out_video as a regular video field.
  remotionComposer: enrichSchema(remotionComposerSchema),
  // Captioner — burns word-anchored captions into a finished video via the
  // hypit sidecar. Has a bespoke component (CaptionerNode) rather than the
  // PlaceholderNode chrome: the two things worth checking after a caption run
  // are the captioned video and the words that were actually aligned, and
  // neither is visible on a placeholder.
  captioner: enrichSchema(captionerSchema),
  // Singing Portrait — makes the subject of a photo (usually a pet) sing a
  // song on self-hosted MiniMax-H3. Bespoke component (SingingPortraitNode):
  // the result clip is the thing to check, and lyrics / performance are the
  // inputs people iterate on, so both live on the node itself.
  singingPortrait: enrichSchema(singingPortraitSchema),
  // Brand Film — the last node of a create_brand_film DAG: paces the
  // inserts, renders the editorial grid and scores it. Bespoke component
  // (BrandFilmNode): the finished film is the only thing to check.
  brandFilm: enrichSchema(brandFilmSchema),

  // Group nodes
  webtoonGroup: enrichSchema(webtoonGroupSchema),
  shortVideoGroup: enrichSchema(shortVideoGroupSchema),
  nodeGroup: enrichSchema(groupSchema),

  // Media/Asset nodes
  image: enrichSchema(imageSchema),
  video: enrichSchema(videoSchema),
  audio: enrichSchema(audioSchema),
  generatedImageWithHistory: enrichSchema(generatedImageWithHistorySchema),

  // Input nodes
  text: enrichSchema(textSchema),
  promptInput: enrichSchema(promptInputSchema),
  panelSource: enrichSchema(panelSourceSchema),
}

// =============================================================================
// Placeholder Node (renders for types not yet ported to canvas-v2)
// =============================================================================

interface PlaceholderNodeData {
  status?: string
  [key: string]: NodeDataValue
}

const PlaceholderNode = memo(function PlaceholderNode({ id, data, type }: NodeProps<PlaceholderNodeData>) {
  const schema = type ? nodeSchemas[type] : undefined
  const label = schema?.label || type || 'Unknown'
  const icon = schema?.icon || '\u{1F4E6}'

  return (
    <div className="rounded-lg shadow bg-card border border-border min-w-[160px] max-w-[280px]">
      <div className="px-3 py-2 bg-muted/50 border-b border-border rounded-t-lg flex items-center gap-2">
        <span className="text-sm">{icon}</span>
        <span className="text-xs font-medium text-foreground truncate">{label}</span>
        <div
          className={`ml-auto w-2 h-2 rounded-full ${
            data?.status === 'running' ? 'bg-yellow-500 animate-pulse' :
            data?.status === 'error' ? 'bg-red-500' :
            data?.status === 'completed' ? 'bg-green-500' :
            'bg-gray-500'
          }`}
        />
      </div>

      {/* Input handles */}
      {schema?.inputs.map((port, i) => (
        <TypedHandle
          key={port.id}
          type="target"
          position={Position.Left}
          id={port.id}
          portType={port.type}
          style={{ top: `${((i + 1) / (schema.inputs.length + 1)) * 100}%` }}
        />
      ))}

      {/* Output handles */}
      {schema?.outputs.map((port, i) => (
        <TypedHandle
          key={port.id}
          type="source"
          position={Position.Right}
          id={port.id}
          portType={port.type}
          style={{ top: `${((i + 1) / (schema.outputs.length + 1)) * 100}%` }}
        />
      ))}

      <div className="px-3 py-2">
        <p className="text-xxs text-muted-foreground">
          {schema?.description || `${label} node`}
        </p>
      </div>
      {id && (
        <div className="px-3 pb-2">
          <p className="text-xxs text-foreground/20 font-mono truncate">{id}</p>
        </div>
      )}
    </div>
  )
})

// =============================================================================
// Unknown Node (fallback for types not in nodeSchemas)
// =============================================================================

const UnknownNode = memo(function UnknownNode(props: NodeProps<NodeDataRecord>) {
  const { type, id, data } = props
  // NAD logging — surface the unknown type so we can fix the registry / schema.
  useEffect(() => {
    unknownLog({
      node_id: id,
      type,
      data_keys: data && typeof data === 'object' ? Object.keys(data) : [],
    })
  }, [id, type, data])
  return (
    <div className="rounded-lg shadow bg-card border border-destructive/50 min-w-[200px] max-w-[280px]">
      <div className="px-3 py-2 bg-destructive/10 border-b border-destructive/30 rounded-t-lg flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden="true" />
        <span className="text-xs font-medium text-destructive">Unknown Node</span>
      </div>
      <div className="px-3 py-3">
        <p className="text-xs text-text-secondary">
          Node type <code className="text-xxs bg-surface-2 px-1 py-0.5 rounded">{type}</code> is not recognized.
        </p>
        <p className="text-xxs text-text-muted mt-1">
          Delete this node or update the app.
        </p>
      </div>
    </div>
  )
})

// =============================================================================
// Node Types Registry (component map for React Flow)
// =============================================================================

type CanvasNodeComponent = React.ComponentType<NodeProps<NodeDataRecord>>

function buildNodeTypes(): Record<string, CanvasNodeComponent> {
  const types: Record<string, CanvasNodeComponent> = {}

  // Map of node types that have real V2 implementations
  const v2Components: Record<string, CanvasNodeComponent> = {
    characterDesigner: CharacterDesignerNode,
    locationDesigner: LocationDesignerNode,
    objectDesigner: ObjectDesignerNode,
    image: ImageUploadNode,
    imageGenerator: ImageGeneratorNode,
    video: VideoUploadNode,
    audio: AudioUploadNode,
    generatedImageWithHistory: ImageGeneratorNode,
    videoGenerator: VideoGeneratorNode,
    soundExpert: SoundExpertNode,
    videoEditor: VideoEditorNode,
    captioner: CaptionerNode,
    singingPortrait: SingingPortraitNode,
    brandFilm: BrandFilmNode,
    scriptWriter: ScriptWriterNode,
    nodeGroup: GroupNode,
    storyboardDesigner: StoryboardDesignerNode,
    storyboardNoEnhanceDesigner: StoryboardDesignerNode,
  }

  for (const [key, schema] of Object.entries(nodeSchemas)) {
    const component = v2Components[key] || PlaceholderNode
    types[key] = withNodeErrorBoundary(component, schema.label)
  }
  // Fallback for node types not in nodeSchemas (e.g. removed or from a newer version)
  types.default = withNodeErrorBoundary(UnknownNode, 'Unknown')
  return types
}

export const nodeTypes = buildNodeTypes()

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get schema for a specific node type.
 * Returns undefined for unknown types.
 */
export function getNodeSchema(type: string): NodeSchema | undefined {
  return nodeSchemas[type]
}

/**
 * Get all nodes grouped by their schema category field.
 * This is the authoritative grouping (BUILD_ORDER Q5).
 */
export function getNodesByCategory(): Record<string, NodeSchema[]> {
  const grouped: Record<string, NodeSchema[]> = {}
  for (const schema of Object.values(nodeSchemas)) {
    const cat = schema.category || 'General'
    if (!grouped[cat]) {
      grouped[cat] = []
    }
    grouped[cat].push(schema)
  }
  return grouped
}

/**
 * List all available node type strings.
 */
export function getAvailableNodeTypes(): string[] {
  return Object.keys(nodeSchemas)
}

export interface CreatedCanvasNode {
  id: string
  type: string
  position: { x: number; y: number }
  data: NodeDataRecord
}

/**
 * Create a new React Flow node object with schema defaults.
 * Uses crypto.randomUUID() for IDs (BUILD_ORDER Q2).
 *
 * @throws Error if the type has no schema
 */
export function createNode(
  type: string,
  position: { x: number; y: number },
  additionalData: NodeDataRecord = {}
): CreatedCanvasNode {
  const schema = nodeSchemas[type]
  if (!schema) {
    throw new Error(`Unknown node type: ${type}`)
  }

  return {
    id: crypto.randomUUID(),
    type,
    position,
    data: {
      ...schema.defaults,
      status: 'idle',
      ...additionalData,
    },
  }
}

/**
 * Get the backend executor ID for a frontend node type.
 * Checks backendNodeId/backendType on schema, else converts camelCase to snake_case.
 */
export function getBackendNodeId(nodeType: string): string {
  const schema = nodeSchemas[nodeType]
  if (schema) {
    if (schema.backendNodeId) return schema.backendNodeId
    if (schema.backendType) return schema.backendType
  }
  // Fallback: convert camelCase to snake_case
  return nodeType.replace(/([A-Z])/g, '_$1').toLowerCase().replace(/^_/, '')
}

/**
 * Check if a node type is executable (has a backend executor).
 * Returns false for unknown types.
 */
export function isNodeExecutable(nodeType: string): boolean {
  const schema = nodeSchemas[nodeType]
  return schema?.executable === true
}

/**
 * Get the layout dimensions for a node type.
 * Looks up the aspect-ratio-specific size first, then falls back to '*' wildcard.
 * Returns a default fallback if the type has no schema or no layout.
 * Also returns footerHeight (per-type footer chrome height, 0 if absent).
 */
export function getNodeDimensions(
  type: string,
  aspectRatio?: string,
): { width: number; height: number; footerHeight: number } {
  const schema = nodeSchemas[type]
  const footerHeight = schema?.footerHeight ?? 0
  if (!schema?.layout) {
    sizeLog('[size] getNodeDimensions: type=%s ratio=%s -> 315x300 (no schema/layout)', type, aspectRatio)
    return { width: 315, height: 300, footerHeight }
  }

  let result: { width: number; height: number; footerHeight: number }
  if (aspectRatio && schema.layout[aspectRatio]) {
    result = { ...schema.layout[aspectRatio], footerHeight }
  } else if (schema.layout['*']) {
    result = { ...schema.layout['*'], footerHeight }
  } else {
    const entries = Object.values(schema.layout)
    if (entries.length > 0) {
      result = { ...entries[0], footerHeight }
    } else {
      result = { width: 315, height: 300, footerHeight }
    }
  }

  sizeLog('[size] getNodeDimensions: type=%s ratio=%s -> %dx%d', type, aspectRatio, result.width, result.height)
  return result
}
